import { type ReactElement, useEffect, useState } from 'react';

import {
  APIProvider,
  AdvancedMarker,
  Map as GMap,
  InfoWindow,
  Pin,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';

import { Dentist } from '../..';
import { useSelectedDentistDataStore } from '../../../../stores/usePaginacao';

const toUpperText = (value: any) =>
  typeof value === 'string' ? value.toLocaleUpperCase('pt-BR') : value;

const formatTelefone = (telefone: any) =>
  typeof telefone === 'string'
    ? telefone
    : Array.isArray(telefone)
    ? telefone
        .map(tel => tel.telefone || tel.descricao || tel.descricaoContato)
        .filter(Boolean)
        .join(' / ')
    : '';

function DentistMarker({
  dentist,
  selected,
  onSelect,
}: {
  dentist: Dentist;
  selected: boolean;
  onSelect: (dentist: Dentist) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [markerRef, marker] = useAdvancedMarkerRef();
  const telefone = formatTelefone(dentist.telefone);
  const clinicName = toUpperText(
    dentist.nomeFantasia ||
      (dentist as any).razaoSocial ||
      dentist.nomeFuncionario,
  );
  const location = [dentist.endereco, dentist.bairro, dentist.municipio]
    .filter(Boolean)
    .map(toUpperText)
    .join(' - ');

  const openWaze = () => {
    if (!dentist.latitude || !dentist.longitude) return;
    window.open(
      'https://waze.com/ul?ll=' +
        dentist.latitude +
        ',' +
        dentist.longitude +
        '&navigate=yes',
      '_blank',
    );
  };

  const openUber = () => {
    if (!dentist.latitude || !dentist.longitude) return;
    window.open(
      'https://m.uber.com/ul/?action=setPickup&dropoff[latitude]=' +
        dentist.latitude +
        '&dropoff[longitude]=' +
        dentist.longitude,
      '_blank',
    );
  };

  useEffect(() => {
    setOpen(selected);
  }, [selected]);

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{
          lat: +dentist.latitude,
          lng: +dentist.longitude,
        }}
        onClick={() => {
          onSelect(dentist);
          setOpen(true);
        }}
      >
        <Pin
          background={selected ? '#272D65' : '#EE6C4D'}
          borderColor={selected ? '#272D65' : '#EE6C4D'}
          glyphColor="white"
        />
      </AdvancedMarker>
      {open && (
        <InfoWindow
          anchor={marker}
          maxWidth={310}
          onCloseClick={() => setOpen(false)}
        >
          <div className="flex min-w-[240px] flex-col gap-3 text-left">
            <div>
              <p className="text-[0.7rem] font-bold uppercase text-medium-emphasis">
                Unidade credenciada
              </p>
              <h4 className="mt-1 text-base font-semibold text-primary-main">
                {clinicName}
              </h4>
            </div>

            <div className="space-y-1 text-sm text-medium-emphasis">
              {location && <p>{location}</p>}
              {telefone && (
                <p className="font-semibold text-high-emphasis">
                  {toUpperText(telefone)}
                </p>
              )}
              {(dentist.especialidades || dentist.especialidade) && (
                <p>
                  {toUpperText(dentist.especialidades || dentist.especialidade)}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                className="rounded-md bg-primary-main px-3 py-2 text-xs font-bold uppercase text-white"
                onClick={() => onSelect(dentist)}
              >
                Destacar na lista
              </button>
              <button
                className="rounded-md border border-neutral-lighter px-3 py-2 text-xs font-bold uppercase text-primary-main"
                onClick={openWaze}
              >
                Waze
              </button>
              <button
                className="rounded-md border border-neutral-lighter px-3 py-2 text-xs font-bold uppercase text-primary-main"
                onClick={openUber}
              >
                Uber
              </button>
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

const apiKey = process.env.REACT_APP_GOOGLE_API_TOKEN;
const mapId = process.env.REACT_APP_GOOGLE_MAP_ID;

interface MapProps {
  docList: any[];
  className?: string;
  showEmptyState?: boolean;
  loading?: boolean;
}

export default function Map({
  docList,
  className,
  showEmptyState = false,
  loading = false,
}: MapProps): ReactElement {
  const selectedDentist = useSelectedDentistDataStore(x => x.data);
  const setSelectedDentist = useSelectedDentistDataStore(
    x => x.setselectedDentistData,
  );
  const [center, setCenter] = useState<{ lat: number; lng: number }>(null);

  useEffect(() => {
    if (selectedDentist?.latitude && selectedDentist?.longitude) {
      handleSelecionarDentista(selectedDentist);
    }
  }, [selectedDentist]);

  const handleSelecionarDentista = (dentist: Dentist) => {
    if (!dentist.latitude || !dentist.longitude) return;

    setCenter({ lat: +dentist.latitude, lng: +dentist.longitude });

    const mapa = document.getElementById('mapa');
    mapa?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  };

  useEffect(() => {
    if (!docList.length) {
      setCenter(null);
      return;
    }

    let lat = 0;
    let lng = 0;
    for (const doc of docList) {
      lat += +doc.latitude;
      lng += +doc.longitude;
    }

    setCenter({ lat: lat / docList.length, lng: lng / docList.length });
    const timer = window.setTimeout(() => setCenter(null), 700);

    return () => window.clearTimeout(timer);
  }, [docList]);

  return (
    <APIProvider apiKey={apiKey}>
      <div
        id="mapa"
        className={
          className
            ? 'relative ' + className
            : 'relative h-[300px] w-full max-[999px]:-order-1 min-[999px]:h-[700px]'
        }
      >
        <GMap
          mapId={mapId}
          defaultZoom={11}
          minZoom={5}
          center={center}
          defaultCenter={{ lat: -15.7801, lng: -47.9292 }}
          style={{ borderRadius: 10 }}
          gestureHandling="greedy"
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl
          zoomControl
        >
          {docList.map(d => (
            <DentistMarker
              key={d.clinicaId || d.idFuncionario}
              dentist={d}
              selected={Boolean(
                selectedDentist?.clinicaId &&
                  d.clinicaId &&
                  String(selectedDentist.clinicaId) === String(d.clinicaId),
              )}
              onSelect={dentist => setSelectedDentist(dentist as any)}
            />
          ))}
        </GMap>

        <div className="pointer-events-none absolute left-4 top-4 rounded-lg bg-white/95 px-4 py-3 text-sm shadow-card">
          <p className="font-bold text-primary-main">
            {loading
              ? 'Buscando unidades...'
              : docList.length > 0
              ? docList.length + ' unidade(s) no mapa'
              : 'Mapa da rede'}
          </p>
          <p className="mt-1 text-xs text-medium-emphasis">
            Clique nos marcadores para ver detalhes.
          </p>
        </div>

        {loading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/40">
            <div className="rounded-lg bg-white/95 px-5 py-4 text-center text-sm font-semibold text-primary-main shadow-card">
              <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-primary-container border-t-primary-main" />
              Carregando resultados
            </div>
          </div>
        )}

        {showEmptyState && docList.length === 0 && !loading && (
          <div className="pointer-events-none absolute inset-x-4 bottom-4 flex justify-center">
            <div className="rounded-lg bg-white/95 px-4 py-3 text-center text-sm font-semibold text-primary-main shadow-card">
              O mapa sera preenchido com os dentistas encontrados.
            </div>
          </div>
        )}
      </div>
    </APIProvider>
  );
}
